<script lang="ts">
  import { adminModuleCss } from "../lib/gruimModuleTheme";
  import { onDestroy, onMount, tick } from "svelte";
  import Progressbar from "./progressbar.svelte";
  import MessageModal from "./messageModal.svelte";
  import { opportunityOptions } from "../lib/opportunityOptionsStore";
  import { syncOpportunityToHubspotById } from "../lib/hubspotAuth";
  import { triggerOpportunityDataRefresh } from "../lib/syncDashboardRefresh";
  import { destroySvelteComponentInstance, mountFederatedSvelteComponent } from "../lib/mountFederatedSvelteComponent";
  import { AWS_APPROVED_READONLY_FIELDS } from "../lib/awsOpportunityUpdateRules";

  const DEFAULT_URL = process.env.SVELTE_APP_REMOTE_URL;

  let usStates: string[] = [];
  let countries: { label: string; value: string }[] = [];

  let el1: HTMLDivElement;
  let el2: HTMLDivElement;
  let el3: HTMLDivElement;
  let customersInstance: any = null;
  let addressesInstance: any = null;
  let contactsInstance: any = null;

  let total = 0;
  let completed = 0;
  let showProgressBar = false;

  let showSyncModal = false;
  let syncMessage: string = "";
  let syncDetails: Record<string, unknown> | null = null;

  const closeModal = (): void => {
    showSyncModal = false;
    syncMessage = "";
    syncDetails = null;
  };

  let showAddress = false;
  let showContacts = false;
  let toggling = false;

  const toggleAddress = async () => {
    toggling = true;
    showAddress = !showAddress;

    await tick();

    if (showAddress && el2) {
      const { default: Addresses } = await import("App/alaceAddresses");
      addressesInstance = mountFederatedSvelteComponent(addressesInstance, Addresses, {
        target: el2,
        props: {
          css: adminModuleCss,
          translation: {
            streetAddress: "Street Address",
            city: "City",
            state: "State",
            postalCode: "Postal Code",
            country: "Country",
          },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "customerId"],
              "field-order": ["streetAddress", "city", "state", "postalCode", "country"],
              "dropdown-fields": [
                {
                  name: "country",
                  options: countries.map((c) => ({ label: c.label, value: c.value })),
                },
                {
                  name: "state",
                  options: usStates.map((s) => ({ label: s, value: s })),
                },
              ],
            },
          },
        },
      });
    } else {
      addressesInstance = destroySvelteComponentInstance(addressesInstance);
    }

    toggling = false;
  };

  const toggleContacts = async () => {
    toggling = true;
    showContacts = !showContacts;

    await tick();

    if (showContacts && el3) {
      const { default: Contacts } = await import("App/alaceContacts");
      contactsInstance = mountFederatedSvelteComponent(contactsInstance, Contacts, {
        target: el3,
        props: {
          css: adminModuleCss,
          translation: {
            firstName: "First Name",
            lastName: "Last Name",
            email: "Email",
            phone: "Phone",
            title: "Title",
          },
          schema: {
            actions: { edit: { removable: true, "with-confirmation": true } },
            "field-properties": {
              "auto-generated-fields": ["id", "customerId"],
              "field-order": ["firstName", "lastName", "email", "phone", "title"],
            },
          },
        },
      });
    } else {
      contactsInstance = destroySvelteComponentInstance(contactsInstance);
    }

    toggling = false;
  };

  const mountCustomers = async () => {
    const { default: Customers } = await import("App/alaceCustomers");
    const opts = await opportunityOptions.fetchOptions();

    if (el1) {
      customersInstance = mountFederatedSvelteComponent(customersInstance, Customers, {
        target: el1,
        props: {
          css: adminModuleCss,
          translation: {
            companyName: "Company Name",
            industryVertical: "Industry Vertical",
            industryOther: "Other Industry",
            website: "Website",
            customerDuns: "DUNS",
            awsAccountId: "AWS Account ID",
            address: "Address",
          },
          schema: {
            actions: {
              edit: { removable: true, "with-confirmation": true },
            },
            "field-properties": {
              "auto-generated-fields": ["id"],
              "readonly-fields": AWS_APPROVED_READONLY_FIELDS.filter((field) =>
                ["industryVertical", "website", "country", "postalCode"].includes(field)
              ),
              "field-order": [
                "companyName",
                "industryVertical",
                "industryOther",
                "website",
                "customerDuns",
                "awsAccountId",
                "address",
              ],
              "relational-fields": [
                {
                  name: "contacts",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["firstName", "lastName", "email", "phone", "title"],
                },
                {
                  name: "addressId",
                  type: "DEFAULT",
                  editable: true,
                  columns: ["streetAddress", "city", "state", "postalCode", "country"],
                  useFuzzySearch: true,
                },
              ],
              "dropdown-fields": [
                {
                  name: "industryVertical",
                  options: opts?.industryVertical?.map((v) => ({ label: v, value: v })) ?? [],
                },
              ],
            },
            "custom-bulk-actions": [
              {
                action: "Sync to AWS",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results = [];
                  for (const customerId of ids) {
                    const oppRes = await fetch(
                      `${DEFAULT_URL}/api/alace/opportunities?filter={"where":{"customerId":${customerId}}}`
                    );
                    const opportunities = await oppRes.json();

                    if (!opportunities.length) {
                      results.push({
                        success: false,
                        message: `No opportunities for customer ${customerId}`,
                      });
                      completed++;
                      continue;
                    }

                    for (const opp of opportunities) {
                      const res = await fetch(
                        `${DEFAULT_URL}/api/alace/sync/opportunity/${opp.id}`,
                        { method: "POST" }
                      );
                      results.push(await res.json());
                    }

                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success !== false)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number }) => {
                  const oppRes = await fetch(
                    `${DEFAULT_URL}/api/alace/opportunities?filter={"where":{"customerId":${row.id}}}`
                  );
                  const opportunities = await oppRes.json();

                  if (!opportunities.length) {
                    syncMessage = "No opportunities for this customer.";
                    syncDetails = {};
                    showSyncModal = true;
                    return { success: false, message: syncMessage };
                  }

                  const results = [];
                  for (const opp of opportunities) {
                    const res = await fetch(`${DEFAULT_URL}/api/alace/sync/opportunity/${opp.id}`, {
                      method: "POST",
                    });
                    results.push(await res.json());
                  }

                  syncMessage = `Customer ${row.id} opportunities synced to AWS`;
                  syncDetails = { synced: results };
                  showSyncModal = true;
                  if (results.some((result) => result?.success !== false)) {
                    triggerOpportunityDataRefresh();
                  }

                  return { success: true, synced: results };
                },
              },
              {
                action: "Sync to HubSpot",
                bulkFn: async (ids: number[]) => {
                  showProgressBar = true;
                  total = ids.length;
                  completed = 0;

                  const results = [];
                  for (const customerId of ids) {
                    const oppRes = await fetch(
                      `${DEFAULT_URL}/api/alace/opportunities?filter={"where":{"customerId":${customerId}}}`
                    );
                    const opportunities = await oppRes.json();

                    if (!opportunities.length) {
                      results.push({
                        success: false,
                        message: `No opportunities for customer ${customerId}`,
                      });
                      completed++;
                      continue;
                    }

                    for (const opp of opportunities) {
                      const result = await syncOpportunityToHubspotById(opp.id);
                      results.push(result);

                      if (result.authRequired) {
                        showProgressBar = false;
                        return results;
                      }
                    }

                    completed++;
                  }

                  showProgressBar = false;
                  if (results.some((result) => result?.success)) {
                    triggerOpportunityDataRefresh();
                  }
                  return results;
                },
                singleFn: async (row: { id: number }) => {
                  const oppRes = await fetch(
                    `${DEFAULT_URL}/api/alace/opportunities?filter={"where":{"customerId":${row.id}}}`
                  );
                  const opportunities = await oppRes.json();

                  if (!opportunities.length) {
                    syncMessage = "No opportunities for this customer.";
                    syncDetails = {};
                    showSyncModal = true;
                    return { success: false, message: syncMessage };
                  }

                  const results = [];
                  for (const opp of opportunities) {
                    const result = await syncOpportunityToHubspotById(opp.id);
                    results.push(result);

                    if (result.authRequired) {
                      return result;
                    }

                    if (!result.success) {
                      syncMessage = result.message || `Failed to sync opportunity ${opp.id} to HubSpot.`;
                      syncDetails = {
                        error: syncMessage,
                        opportunityId: opp.id,
                      };
                      showSyncModal = true;

                      return {
                        success: false,
                        message: syncMessage,
                      };
                    }
                  }

                  syncMessage = `Customer ${row.id} opportunities synced to HubSpot`;
                  syncDetails = { synced: results };
                  showSyncModal = true;
                  if (results.some((result) => result?.success)) {
                    triggerOpportunityDataRefresh();
                  }

                  return { success: true, synced: results };
                },
              },
            ],
          },
        },
      });
    }
  };

  const fetchCountries = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/alace/sync/countries`);
      if (!res.ok) throw new Error("Failed to load countries");
      countries = await res.json();
    } catch (e) {
      console.error("Failed to load countries", e);
    }
  };

  const fetchUsStates = async (): Promise<void> => {
    try {
      const res = await fetch(`${DEFAULT_URL}/api/alace/sync/us-states`);
      if (!res.ok) throw new Error("Failed to load US states");
      usStates = await res.json();
    } catch (e) {
      console.error("Failed to load US states", e);
    }
  };
  onMount(async () => {
    try {
      await Promise.all([fetchUsStates(), fetchCountries(), mountCustomers()]);
    } catch (err) {
      console.error("Initialization error:", err);
    }
  });

  onDestroy(() => {
    customersInstance = destroySvelteComponentInstance(customersInstance);
    addressesInstance = destroySvelteComponentInstance(addressesInstance);
    contactsInstance = destroySvelteComponentInstance(contactsInstance);
  });
</script>

{#if showProgressBar}
  <Progressbar {completed} {total} />
{/if}

<div bind:this={el1} />

<button
  on:click={toggleAddress}
  class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 mt-3 mr-2"
  disabled={toggling}
>
  {toggling ? "Loading..." : showAddress ? "Hide Addresses" : "Show Addresses"}
</button>

<button
  on:click={toggleContacts}
  class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 mt-3 mr-2"
  disabled={toggling}
>
  {toggling ? "Loading..." : showContacts ? "Hide Contacts" : "Show Contacts"}
</button>

{#if showAddress}
  <div bind:this={el2} />
{/if}

{#if showContacts}
  <div bind:this={el3} />
{/if}

<MessageModal
  show={showSyncModal}
  title="Sync Result"
  message={syncMessage}
  details={syncDetails}
  onClose={closeModal}
/>
